import { Box, Divider, Flex, Heading, Stack, Text, Tooltip, useColorModeValue as mode } from '@chaine/keychaine'
import React, { useMemo } from 'react'

import { IPerformanceMetrics } from '../types'

interface IMetricsDisplayProps {
  metrics: IPerformanceMetrics
  title?: string
}

// Base component for displaying metrics with shared tooltip logic
const MetricDisplay = ({
  label,
  value,
  tooltip,
  valueFormatter
}: {
  label: string
  tooltip?: string
  value: number | null
  valueFormatter: (value: number) => string
}) => {
  const showTooltip = !!tooltip && value !== null

  return (
    <Tooltip
      label={tooltip}
      hasArrow
      placement="top"
      isDisabled={!showTooltip}
      openDelay={300}
      closeDelay={100}
      bg="gray.700"
      color="white"
      borderRadius="md"
      fontSize="xs"
      maxW="300px"
      px={2}
      py={1}
    >
      <Flex justify="space-between" py={1}>
        <Text fontSize="sm" fontWeight="medium">
          {label}
        </Text>
        <Text fontSize="sm" fontWeight="bold">
          {value !== null ? valueFormatter(value) : '--'}
        </Text>
      </Flex>
    </Tooltip>
  )
}

// Use specialized formatters for different metric types
const formatPercentage = (value: number) => `${value.toFixed(0)}%`
const formatMinutes = (value: number) => `${value.toFixed(1)} min`

export const MetricsDisplay: React.FC<IMetricsDisplayProps> = ({ metrics, title }) => {
  const { onTimePerformance, trackingPerformance } = metrics
  const headingColor = mode('gray.700', 'white')
  const subheadingColor = mode('gray.600', 'gray.400')

  // Memoize the configuration data structure to avoid recreating on each render
  const metricsConfig = useMemo(
    () => [
      {
        subsections: [
          {
            metrics: [
              {
                key: 'recentOnTimePercentageOverall',
                label: 'Overall On-Time Percentage',
                tooltip: 'Recent percentage of on-time pickups and deliveries'
              },
              {
                key: 'recentOnTimePickupPercentage',
                label: 'On-Time Pickup Percentage'
              },
              {
                key: 'recentOnTimeDeliveryPercentage',
                label: 'On-Time Delivery Percentage'
              },
              {
                key: 'recentAverageMinutesDelayedForPickup',
                label: 'Average Minutes Delayed (Pickup)',
                timeFormat: true,
                tooltip: 'Recent average minutes delayed for pickups'
              },
              {
                key: 'recentAverageMinutesDelayedForDelivery',
                label: 'Average Minutes Delayed (Delivery)',
                timeFormat: true,
                tooltip: 'Recent average minutes delayed for deliveries'
              }
            ],
            title: 'Recent Performance'
          },
          {
            metrics: [
              {
                key: 'totalOnTimePercentageOverall',
                label: 'Overall On-Time Percentage',
                tooltip: 'Historical percentage of on-time pickups and deliveries'
              },
              {
                key: 'totalOnTimePickupPercentage',
                label: 'On-Time Pickup Percentage'
              },
              {
                key: 'totalOnTimeDeliveryPercentage',
                label: 'On-Time Delivery Percentage'
              },
              {
                key: 'totalAverageMinutesDelayedOverall',
                label: 'Average Minutes Delayed (Overall)',
                timeFormat: true,
                tooltip: 'Historical average minutes delayed overall'
              },
              {
                key: 'totalAverageMinutesDelayedForPickup',
                label: 'Average Minutes Delayed (Pickup)',
                timeFormat: true,
                tooltip: 'Historical average minutes delayed for pickups'
              },
              {
                key: 'totalAverageMinutesDelayedForDelivery',
                label: 'Average Minutes Delayed (Delivery)',
                timeFormat: true,
                tooltip: 'Historical average minutes delayed for deliveries'
              }
            ],
            title: 'Total Performance'
          }
        ],
        title: 'On-Time Performance'
      },
      {
        subsections: [
          {
            metrics: [
              {
                key: 'recentTrackingCompliancePercentage',
                label: 'Tracking Compliance',
                tooltip: 'Recent percentage of loads properly tracked'
              },
              {
                key: 'recentPercentageOfFakeLoadsTracked',
                label: 'Fake Loads Tracked',
                tooltip: 'Recent percentage of fake loads that were tracked'
              }
            ],
            title: 'Recent Performance'
          },
          {
            metrics: [
              {
                key: 'totalTrackingCompliancePercentage',
                label: 'Tracking Compliance',
                tooltip: 'Historical percentage of loads properly tracked'
              },
              {
                key: 'totalPercentageOfFakeLoadsTracked',
                label: 'Fake Loads Tracked',
                tooltip: 'Historical percentage of fake loads that were tracked'
              }
            ],
            title: 'Total Performance'
          },
          {
            metrics: [
              {
                key: 'totalTrackedMobileAppPercentage',
                label: 'Mobile App Tracking'
              },
              {
                key: 'totalTrackedViaELDPercentage',
                label: 'ELD Tracking'
              },
              {
                key: 'totalTrackedViaAPIPercentage',
                label: 'API Tracking'
              },
              {
                key: 'totalTrackedIOTDevicePercentage',
                label: 'IOT Device Tracking'
              },
              {
                key: 'totalTrackedViaExternalTrackingLinkPercentage',
                label: 'External Link Tracking'
              },
              {
                key: 'totalTrackedViaSFTPPercentage',
                label: 'SFTP Tracking'
              }
            ],
            title: 'Tracking Methods'
          }
        ],
        title: 'Tracking Performance'
      }
    ],
    []
  )

  // Helper function to get metric value by its key path
  const getMetricValue = (section: string, key: string) => {
    const source = section === 'On-Time Performance' ? onTimePerformance : trackingPerformance
    return source[key as keyof typeof source]
  }

  // Check if the metric key contains "percentage"
  const shouldShowPercentage = (key: string) => key.toLowerCase().includes('percentage')

  return (
    <Box>
      {title && (
        <Heading size="lg" mb={5} color={headingColor} fontWeight="semibold">
          {title} Performance metrics
        </Heading>
      )}

      <Stack spacing={6}>
        {metricsConfig.map((section) => (
          <Box key={section.title}>
            <Heading size="md" mb={3} color={headingColor} fontWeight="semibold">
              {section.title}
            </Heading>
            <Divider mb={section.title === 'On-Time Performance' ? 2 : 4} />

            <Stack spacing={2}>
              {section.subsections.map((subsection) => (
                <React.Fragment key={subsection.title}>
                  <Heading size="sm" color={subheadingColor} pt={3} mb={1} fontWeight="medium">
                    {subsection.title}
                  </Heading>
                  {subsection.metrics.map((metric) => {
                    const value = getMetricValue(section.title, metric.key)
                    const isTimeFormat = metric.timeFormat === true
                    const isPercentage = !isTimeFormat && shouldShowPercentage(metric.key)

                    return (
                      <MetricDisplay
                        key={metric.key}
                        label={metric.label}
                        value={value}
                        tooltip={metric.tooltip}
                        valueFormatter={
                          isTimeFormat ? formatMinutes : isPercentage ? formatPercentage : (v) => v.toFixed(0)
                        }
                      />
                    )
                  })}
                </React.Fragment>
              ))}
            </Stack>
          </Box>
        ))}
      </Stack>
    </Box>
  )
}
